import ApiGlobalSettings, {ApiSettings_EatIn} from "../@types/api/settings";
import config from "../config/default.json";
import {ApiFilter_allergen, ApiFilter_preference} from "../@types/api/filter";
import {Combination, HalfNHalfArticle, MenuItem, MenuItemCombination, MenuSection} from "../@types/stores/menu";
import {ApiCombinationOrderLine, ApiOrderLine} from "../@types/api/order";
import {CombinationOrderLine, HalfAndHalfOrderLine, OrderLine} from "../@types/stores/order";
import {LogLevel} from "../@types/enums/logLevel";
import moment from "moment";
import menuStore from "../stores/menu";
import {CashRegisterMode} from "../@types/enums/cashRegisterMode";

export const getMaxQuantityInOrderLine = (): number => {
    const string = localStorage.getItem("settings");
    if (string) {

        try {
            const settings = JSON.parse(string) as ApiGlobalSettings;
            return settings.maxQuantityInOrderLine;
        } catch (e) {
            console.error(e);
        }
    }

    return config.settings.maxQuantityInOrderLine
}

export const getMaxAddonsCount = (): number => {
    const string = localStorage.getItem("settings");
    if (string) {

        try {
            const settings = JSON.parse(string) as ApiGlobalSettings;
            return settings.maxAddonsCount;
        } catch (e) {
            console.error(e);
        }
    }

    return config.settings.maxAddonsCount
}

export const isExpensiveFree = (): boolean => {
    const string = localStorage.getItem("settings");
    if (string) {
        try {
            const settings = JSON.parse(string) as ApiGlobalSettings;
            return settings.addonsSumMostExpensive;
        } catch (e) {
            console.error(e);
        }
    }

    return true;
}

export const getHost = (): string => {
    return localStorage.getItem("host") || "";
}

export const getCurrency = (): string => {
    try {
        const settings = JSON.parse(localStorage.getItem("settings") || "") as unknown as ApiGlobalSettings;
        return settings?.currency || "";
    } catch (e) {
        return "";
    }
}

export const getAllowedTypes = () => {
    return localStorage.getItem("allowedMenuTypes") || "";
}

export const getAllergenById = (id: number): ApiFilter_allergen | null => {
    try {
        const allAllergensStr = localStorage.getItem("filterAllergens");
        if (allAllergensStr) {
            const _allAllergens = JSON.parse(allAllergensStr) as ApiFilter_allergen[];
            return _allAllergens.find(x => x.id === id) || null;
        }
    } catch {
        //ignore
    }

    return null;
}

export const getPreferenceById = (id: number): ApiFilter_preference | null => {
    try {
        const allPreferencesStr = localStorage.getItem("filterPreferences");
        if (allPreferencesStr) {
            const _allPreferences = JSON.parse(allPreferencesStr) as ApiFilter_preference[];
            return _allPreferences.find(x => x.id === id) || null;
        }
    } catch {
        //ignore
    }
    return null;
}

export const isMenuItem = (item: MenuItem | MenuItemCombination): boolean => {
    return !("combination" in item);
}

export const isMenuItemCombination = (item: MenuItem | MenuItemCombination): boolean => {
    return "combination" in item;
}

export const isApiOrderLine = (item: ApiOrderLine | ApiCombinationOrderLine): boolean => {
    return !("combination" in item);
}

export const isApiCombinationOrderLine = (item: ApiOrderLine | ApiCombinationOrderLine): boolean => {
    return "combination" in item;
}

export const isOrderLine = (item: OrderLine | CombinationOrderLine | HalfAndHalfOrderLine): boolean => {
    return !("steps" in item);
}

export const isCombinationOrderLine = (item: OrderLine | CombinationOrderLine | HalfAndHalfOrderLine): boolean => {
    return "combinationId" in item;
}

export const isHalfAndHalfOrderLine = (item: OrderLine | CombinationOrderLine | HalfAndHalfOrderLine): boolean => {
    return "steps" in item && !("combinationId" in item);
}

export const getNextCombinationStep = (combination: MenuItemCombination, step: Combination): Combination | undefined => {
    const curStepIndex = combination.combination.findIndex(x => x.id === step.id);
    if (curStepIndex === -1) return;
    if (combination.combination.length - 1 === curStepIndex) return;
    return combination.combination[curStepIndex + 1];
}

export const hasArticleOptions = (article: MenuItem): boolean => {
    return !!(article.modifierGroups?.length || article.addonGroups?.length || article.ingredientGroups?.length);
}

export const getPackageArticles = (combination: MenuItemCombination): { step: Combination, article: MenuItem }[] => {
    const articles: { step: Combination, article: MenuItem }[] = [];
    combination.combination.forEach(step => step.articles.forEach(ca => articles.push({step, article: ca.article})));
    return articles;
}

export const getCombinationArticleOptionsUrl = (menuId: number, subMenuId: number, combination: MenuItemCombination, step: Combination, article: MenuItem, isChange: boolean = false, after?: "modifiers" | "addons" | "ingredients"): string | undefined => {
    const url = `/menu/combination/${menuId}/${subMenuId}/${combination.id}/step/${step.id}/article/${article.id}`;
    const change = isChange ? "/change" : "";

    if (!after && article.modifierGroups?.length)
        return `${url}/modifiers${change}`;
    if (after !== "ingredients" && after !== "addons" && article.addonGroups?.length)
        return `${url}/addons${change}`;
    if (after !== "ingredients" && article.ingredientGroups?.length)
        return `${url}/ingredients${change}`;

    return;
}

// next package article (through all steps) with modifiers / addons / ingredients
export const getNextPackageArticleOptionsUrl = (menuId: number, subMenuId: number, combination: MenuItemCombination, step?: Combination, article?: MenuItem): string => {
    const articles = getPackageArticles(combination);
    const curIndex = step && article
        ? articles.findIndex(x => x.step.id === step.id && x.article.id === article.id)
        : -1;

    for (const next of articles.slice(curIndex + 1)) {
        const url = getCombinationArticleOptionsUrl(menuId, subMenuId, combination, next.step, next.article);
        if (url) return url;
    }

    return `/menu/combination/info`;
}

export const getAdditionalInformation = (): string => {
    const string = localStorage.getItem("settings");
    if (string) {
        try {
            const settings = JSON.parse(string) as ApiGlobalSettings;
            return settings.additionalInformationToCustomer || "";
        } catch (e) {
            console.error(e);
        }
    }

    return "";
}

export const addToLog = (text: string, level: LogLevel = LogLevel.INFO) => {
    if (!isSendLog()) return;

    let logs = localStorage.getItem("logs") ? localStorage.getItem("logs") as string : "";
    logs += `${moment().toISOString()} [${level}] ${text}\n`;
    localStorage.setItem("logs", logs);
}

export const getLogs = () => {
    return localStorage.getItem("logs") ? localStorage.getItem("logs") as string : "";
}

export const clearLogs = () => {
    localStorage.removeItem("logs");
}

export const getCurrentCategory = () => {
    return menuStore.menu?.find(x => x.id === menuStore.navigation.category);
}

export const getCurrentSubCategory = () => {
    const category = menuStore.menu?.find(x => x.id === menuStore.navigation.category);
    return category?.submenu?.find(x => x.id === menuStore.navigation.subCategory);
}

export const isSendLog = (): boolean => {
    const string = localStorage.getItem("settings");
    if (string) {
        try {
            const settings = JSON.parse(string) as ApiGlobalSettings;
            return !!settings.enableLogs;
        } catch (e) {
            console.error(e);
        }
    }

    return false;
}

export const getEatInSettings = (): ApiSettings_EatIn | undefined => {
    const string = localStorage.getItem("settings");
    if (string) {
        try {
            const settings = JSON.parse(string) as ApiGlobalSettings;
            return settings.eatIn;
        } catch (e) {
            console.error(e);
        }
    }

    return;
}

export const isRetail = (): boolean => {
    const string = localStorage.getItem("settings");
    if (string) {
        try {
            const settings = JSON.parse(string) as ApiGlobalSettings;
            return settings.cashRegisterMode === CashRegisterMode.RETAIL;
        } catch (e) {
            console.error(e);
        }
    }

    return false;
}

export const isArticleHasPreferences = (article: MenuItem | MenuItemCombination): boolean => {
    if (!menuStore.filter.preferences.length)
        return true;
    if (!article.preferences || article.preferences.length === 0)
        return false;

    for (const y of menuStore.filter.preferences) {
        if (article.preferences.indexOf(y) === -1)
            return false;
    }
    return true;
}

export const isArticleHasAllergens = (article: MenuItem | MenuItemCombination): boolean => {
    if (!menuStore.filter.allergens.length)
        return false;
    if (!article.allergens || article.allergens.length === 0)
        return false;

    for (const y of menuStore.filter.allergens) {
        if (article.allergens.indexOf(y) === -1)
            return false;
    }
    return true;
}

export const subCategoryHasHalfAndHalf = (subCategory: MenuSection): boolean => {
    if (!menuStore.menu) return false;

    const articles = (subCategory.articles?.filter(x => isMenuItem(x) && (x as MenuItem).combinationGroups?.length) || []) as MenuItem[];
    for (const article of articles) {
        for (const menu of menuStore.menu) {
            if (menu.submenu) {
                for (const subMenu of menu.submenu) {
                    const hasCombinationArticles = !!subMenu.articles?.filter(x => isMenuItem(x) && x.id !== article.id && (x as MenuItem).combinationGroups?.some(y => article.combinationGroups?.includes(y)));
                    if (hasCombinationArticles)
                        return true;
                }
            }
        }
    }
    return false;
}

export const getArticlesByCombinationGroup = (group: number[]): HalfNHalfArticle[] => {
    const hAhArticles: HalfNHalfArticle[] = [];

    if (menuStore.menu) {
        for (const menu of menuStore.menu) {
            if (menu.submenu) {
                for (const subMenu of menu.submenu) {
                    const articles = (subMenu.articles?.filter(x => isMenuItem(x) && group.includes((x as MenuItem).typeId || -1)) || []) as MenuItem[];
                    articles.forEach(article => {
                        if (!hAhArticles.some(x => x.article.id === article.id)) {
                            hAhArticles.push({
                                categoryId: menu.id,
                                subCategoryId: subMenu.id,
                                article
                            });
                        }
                    })
                }
            }
        }
    }

    return hAhArticles;
}

export const getHalfAndHalfFromSubCategory = (categoryId: number, subCategoryId: number): HalfNHalfArticle[] => {
    const hAhArticles: HalfNHalfArticle[] = [];

    if (menuStore.menu) {
        const subCategory = menuStore.menu.find(x => x.id === categoryId)?.submenu?.find(x => x.id === subCategoryId) || null;
        if (subCategory) {
            const articles = (subCategory.articles?.filter(x => isMenuItem(x) && (x as MenuItem).combinationGroups?.length) || []) as MenuItem[];
            articles.forEach(article => {
                if (article.combinationGroups?.length) {
                    const combinationArticles = getArticlesByCombinationGroup(article.combinationGroups);
                    if (combinationArticles.length > 1) {
                        hAhArticles.push({
                            categoryId,
                            subCategoryId: subCategory.id,
                            article
                        });
                    }
                }
            });
        }
    }

    return hAhArticles;
}

export const uniqArray = <T, K extends keyof T>(arr: T[], key: K): T[] => {
    const seen = new Map<T[K], T>();

    for (const item of arr) {
        const value = item[key];
        if (!seen.has(value)) {
            seen.set(value, item);
        }
    }

    // @ts-ignore
    return [...seen.values()];
}

export const getAllSettings = (): ApiGlobalSettings | null => {
    const string = localStorage.getItem("settings");
    if (string) {
        try {
            // return Object.assign(JSON.parse(string) as ApiGlobalSettings, {disallowHnHInDifferentUnits: true});
            return JSON.parse(string) as ApiGlobalSettings;
        } catch (e) {
            console.error(e);
        }
    }
    return null;
}
